import { addMethod, string } from 'yup';
import i18n from '@/lang/i18nPlugin';
import { blackList } from '@/shared/Validierung/ValidierungsRegeln/zeichenRegexListen';

const emailLocalPartRegex = /[a-zA-Z0-9._+-]/;

const emailRegex = /^[a-zA-Z0-9]+([._+-][a-zA-Z0-9]+)*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

export const getInvalideCharacters = (value: string, regexp: RegExp): string[] => {
  if (!value) {
    return [];
  }

  const characters = Array.from(new Set(value.split('')));

  const invalideZeichen: string[] = [];

  characters.forEach((c) => {
    if (!c.match(regexp)) {
      invalideZeichen.push(c);
    }
  });

  return invalideZeichen;
};

export const getBlackListMatches = (value: string) => {
  if (!value) {
    return [];
  }

  const matches = value.match(blackList);
  if (!matches || matches.length === 0) {
    return [];
  }

  return Array.from(new Set(matches));
};

addMethod(string, 'matchExpression', function matchExpression(regexp: RegExp, message?: string) {
  return this.test({
    test(value) {
      // Für alle falsy Values geben wir true zurück, da hier nichts negativ matchen kann
      if (!value) {
        return true;
      }

      const invalideZeichen = getInvalideCharacters(value, regexp);

      if (invalideZeichen.length !== 0) {
        if (message) {
          return this.createError({ message });
        }

        if (invalideZeichen.length === 1) {
          return this.createError({
            message: i18n.global.t('shared.validation.characters.singleCharacterNotAllowed', {
              characters: invalideZeichen[0],
            }),
          });
        }
        return this.createError({
          message: i18n.global.t('shared.validation.characters.multipleCharactersNotAllowed', {
            characters: invalideZeichen.join(', '),
          }),
        });
      }

      return true;
    },
  });
});

addMethod(string, 'matchBlacklist', function matchBlacklist() {
  return this.test({
    test(value) {
      if (!value) {
        return true;
      }

      const invalideZeichen = getBlackListMatches(value);

      if (invalideZeichen.length !== 0) {
        if (invalideZeichen.length === 1) {
          return this.createError({
            message: i18n.global.t('shared.validation.characters.singleCharacterNotAllowed', {
              characters: invalideZeichen[0],
            }),
          });
        }
        return this.createError({
          message: i18n.global.t('shared.validation.characters.multipleSequencesNotAllowed', {
            characters: invalideZeichen.join(', '),
          }),
        });
      }

      return true;
    },
  });
});

/**
 * Wir überschreiben die original yup Methode 'email' da diese nach dem HTML Spec validiert, das
 * ist aber nicht ausreichend für unsere Zwecke.
 */
addMethod(string, 'email', function validateEmail(message) {
  return this.matches(emailRegex, {
    name: 'email',
    message: message ?? i18n.global.t('shared.validation.email.invalid'),
    excludeEmptyString: true,
  }).test({
    name: 'email-local-part-characters',
    skipAbsent: true,
    test(value) {
      const localPart = (value ?? '').split('@')[0];
      const invalidCharacters = getInvalideCharacters(localPart, emailLocalPartRegex);

      if (invalidCharacters.length === 0) {
        return true;
      }

      return this.createError({
        message: i18n.global.t('shared.validation.email.localPartCharactersNotAllowed', {
          characters: invalidCharacters.join(', '),
        }),
      });
    },
  });
});

/**
 * Erweiterung von yup um die validPercentage Validierung
 */

addMethod(string, 'validPercentage', function validatePercentage() {
  return this.test({
    name: 'validPercentage',
    message: i18n.global.t('shared.validation.percentage.invalid'),
    test: (value) => {
      if (!value) {
        return true;
      }

      return !Number.isNaN(value) && Number(value) >= 0.01 && Number(value) <= 100;
    },
  });
});
